import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import EllipsisTooltip from "../Components/Common/EllipsisTooltip";

export const getFacilityName = (item) =>
  item?.facility?.facilityName ||
  item?.facilityName?.facilityName ||
  (typeof item?.facilityName === "string" ? item.facilityName : "") ||
  "";

export const shouldShowFacilityColumn = (user) =>
  Boolean(user?.companyAdmin) && !user?.facility;

export const FACILITY_SORT_FIELD = "facilityName";

export const facilityHeaderCellSx = {
  fontWeight: "bold",
  padding: "8px 12px",
  textAlign: "left",
  borderRight: "1px solid #ddd",
  whiteSpace: "normal",
  wordBreak: "break-word",
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  overflow: "hidden",
  boxSizing: "border-box",
};

export const facilityDataCellSx = {
  padding: "8px 12px",
  textAlign: "left",
  borderRight: "1px solid #ddd",
  whiteSpace: "normal",
  wordBreak: "break-word",
  fontSize: "14px",
  color: "black",
  lineHeight: 1.4,
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  overflow: "hidden",
  boxSizing: "border-box",
};

/** Fixed min width so Action header + menu icon stay visible in grid tables. */
export const GRID_TABLE_ACTION_COL = "minmax(56px, 0.65fr)";

/** Paper wrapper for custom CSS-grid list tables (avoids TableContainer horizontal scroll). */
export const gridTablePaperSx = {
  maxWidth: "100%",
  width: "100%",
  marginBottom: "1rem",
  overflow: "hidden",
  boxSizing: "border-box",
};

/** Grid row/header styles — keeps columns within container width. */
export const getGridTableRowSx = (columnWidth) => ({
  display: "grid",
  gridTemplateColumns: columnWidth,
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  alignItems: "stretch",
  "& > *:not(:last-child)": {
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
  },
  "& > *:last-child": {
    minWidth: 0,
    boxSizing: "border-box",
    overflow: "visible",
  },
});

export const gridTableActionHeaderSx = {
  fontWeight: "bold",
  padding: "8px 12px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  alignSelf: "stretch",
  minHeight: "100%",
  height: "100%",
  minWidth: 0,
  boxSizing: "border-box",
};

export const gridTableActionCellSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4px 8px",
  minWidth: 0,
  boxSizing: "border-box",
  overflow: "visible",
};

export const FacilityColumnHeading = ({
  handleSort,
  sortConfig,
  sortField = FACILITY_SORT_FIELD,
  title = "Facility Name",
}) => (
  <Typography
    sx={{
      ...facilityHeaderCellSx,
      flexDirection: "row",
      gap: "6px",
      minWidth: 0,
      minHeight: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
    }}
  >
    <Box component="span" sx={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
      {title}
    </Box>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flexShrink: 0,
        width: "22px",
      }}
    >
      <IconButton
        size="small"
        sx={{ p: 0, height: "14px", width: "22px" }}
        onClick={() => handleSort(sortField, "asc")}
      >
        <ExpandLess
          sx={{
            fontSize: "18px",
            color:
              sortConfig.key === sortField && sortConfig.direction === "asc"
                ? "blue"
                : "black",
          }}
        />
      </IconButton>
      <IconButton
        size="small"
        sx={{ p: 0, height: "14px", width: "22px" }}
        onClick={() => handleSort(sortField, "desc")}
      >
        <ExpandMore
          sx={{
            fontSize: "18px",
            color:
              sortConfig.key === sortField && sortConfig.direction === "desc"
                ? "blue"
                : "black",
          }}
        />
      </IconButton>
    </Box>
  </Typography>
);

export const withFacilityHeading = (
  headings,
  showFacility,
  key = "fieldName",
) => {
  if (!showFacility) return headings;
  const facilityHeading =
    key === "field"
      ? { title: "Facility Name", field: "facilityName" }
      : { fieldName: "facilityName", title: "Facility Name" };
  return [headings[0], facilityHeading, ...headings.slice(1)];
};

export const isFacilityHeading = (el) =>
  el?.fieldName === "facilityName" ||
  el?.field === "facilityName" ||
  el?.key === "facilityName";

export const FACILITY_VIEW_TABLE_COLUMN = {
  key: "facilityName",
  label: "Facility Name",
  sortable: true,
  width: "10rem",
};

export const getVendorMasterColumnWidth = (showFacility) =>
  showFacility
    ? `minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 1.2fr) ${GRID_TABLE_ACTION_COL}`
    : `minmax(0, 0.9fr) minmax(0, 1fr) ${GRID_TABLE_ACTION_COL}`;

export const getEquipmentMasterColumnWidth = (showFacility) =>
  showFacility
    ? `minmax(0, 0.9fr) minmax(0, 1fr) minmax(0, 1.1fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1fr) ${GRID_TABLE_ACTION_COL}`
    : `minmax(0, 0.9fr) minmax(0, 1.2fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 0.9fr) minmax(0, 1.1fr) ${GRID_TABLE_ACTION_COL}`;

export const masterDateDataCellSx = {
  ...facilityDataCellSx,
  paddingLeft: "16px",
};

export const FacilityDataCell = ({ children, title, sx = {} }) => {
  const value = children ?? "N/A";
  return (
    <EllipsisTooltip
      title={title ?? value}
      sx={{ ...facilityDataCellSx, ...sx }}
    >
      {value}
    </EllipsisTooltip>
  );
};

export const MasterDateDataCell = ({ children, title, sx = {} }) => (
  <EllipsisTooltip
    title={title ?? children}
    sx={{ ...masterDateDataCellSx, ...sx }}
  >
    {children}
  </EllipsisTooltip>
);

export const getVendorMasterHeaderRowSx = (columnWidth) => ({
  ...getGridTableRowSx(columnWidth),
  borderBottom: "1px solid #ddd",
  borderTop: "1px solid #ddd",
  "& > .MuiTypography-root:not(:last-child)": {
    height: "100%",
    minHeight: 36,
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
    minWidth: 0,
  },
});

export const getEquipmentMasterHeaderRowSx = getVendorMasterHeaderRowSx;

/** Inserts a facility column after the first ViewTable header when admin has no assigned facility. */
export const withFacilityViewTableHeader = (
  headers,
  showFacility,
  options = {},
) => {
  if (!showFacility || !headers?.length) return headers;
  const facilityColumn = {
    ...FACILITY_VIEW_TABLE_COLUMN,
    label: options.label ?? FACILITY_VIEW_TABLE_COLUMN.label,
    width: options.width ?? FACILITY_VIEW_TABLE_COLUMN.width,
    ...(options.fluidWeight != null
      ? { fluidWeight: options.fluidWeight }
      : {}),
  };
  return [headers[0], facilityColumn, ...headers.slice(1)];
};

/** Maps list rows so ViewTable can render facilityName for company admins. */
export const mapTableDataWithFacility = (data, showFacility) => {
  if (!showFacility) return data ?? [];
  return (data ?? []).map((row) => ({
    ...row,
    facilityName: getFacilityName(row?.data ?? row),
  }));
};
